import type { ReactNode } from 'react';
import { C, DEGRADE_ACTION, OMBRE_ACTION } from '../../theme';
import { Micro, NavAujourdhui, NavBoite, NavPlanning, NavColette } from '../../icones';

/** Les quatre destinations de la barre du bas. */
export type Ecran = 'aujourdhui' | 'boite' | 'planning' | 'reglages';

function Onglet({
  actif,
  libelle,
  icone,
  onClick,
  compte = 0,
}: {
  actif: boolean;
  libelle: string;
  icone: (couleur: string) => ReactNode;
  onClick: () => void;
  /** Nombre de choses qui attendent un geste. 0 = pas de pastille. */
  compte?: number;
}) {
  // La barre ne suit PAS le theme (handoff) : elle est blanche en permanence, et ses
  // couleurs sont donc fixes elles aussi — un `faint` translucide y serait illisible.
  const couleur = actif ? C.corailFixe : C.tertiaireFixe;
  return (
    <button
      type="button"
      onClick={onClick}
      aria-current={actif ? 'page' : undefined}
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 3,
        width: 62,
        paddingTop: 12,
        background: 'none',
        border: 'none',
        fontFamily: 'inherit',
        cursor: 'pointer',
      }}
    >
      <span style={{ position: 'relative', display: 'flex' }}>
        {icone(couleur)}
        {compte > 0 ? (
          <span
            aria-hidden="true"
            style={{
              position: 'absolute',
              top: -5,
              right: -9,
              minWidth: 17,
              height: 17,
              borderRadius: 999,
              background: C.corailFixe,
              color: '#FFFFFF',
              fontSize: 11,
              fontWeight: 800,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '0 4px',
            }}
          >
            {compte > 9 ? '9+' : compte}
          </span>
        ) : null}
      </span>
      <span style={{ fontSize: 10, fontWeight: 800, color: couleur }}>{libelle}</span>
    </button>
  );
}

export function BarreNav({
  ecran,
  aller,
  ouvrirPanneau,
  aValider = 0,
}: {
  ecran: Ecran;
  aller: (e: Ecran) => void;
  ouvrirPanneau: () => void;
  /** Ce qui attend un geste dans la Boite : la pastille de l'onglet. */
  aValider?: number;
}) {
  return (
    <nav
      style={{
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'space-between',
        padding: '0 18px 18px 18px',
        borderTop: `1px solid ${C.bordureFixe}`,
        background: C.surfaceFixe,
        flexShrink: 0,
        // Au-dessus du halo decoratif du cadre, qui est en z-index 0.
        position: 'relative',
        zIndex: 1,
      }}
    >
      <Onglet
        actif={ecran === 'aujourdhui'}
        libelle="Aujourd'hui"
        icone={(c) => <NavAujourdhui taille={22} couleur={c} />}
        onClick={() => aller('aujourdhui')}
      />
      <Onglet
        actif={ecran === 'boite'}
        libelle="Boîte"
        icone={(c) => <NavBoite taille={22} couleur={c} />}
        onClick={() => aller('boite')}
        compte={aValider}
      />

      <div style={{ width: 62, display: 'flex', justifyContent: 'center' }}>
        <button
          type="button"
          onClick={ouvrirPanneau}
          aria-label="Organise-moi ça"
          style={{
            width: 60,
            height: 60,
            borderRadius: '50%',
            background: DEGRADE_ACTION,
            border: 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginTop: -30,
            boxShadow: OMBRE_ACTION,
            cursor: 'pointer',
          }}
        >
          <Micro taille={26} couleur="#FFFFFF" />
        </button>
      </div>

      <Onglet
        actif={ecran === 'planning'}
        libelle="Planning"
        icone={(c) => <NavPlanning taille={22} couleur={c} />}
        onClick={() => aller('planning')}
      />
      <Onglet
        actif={ecran === 'reglages'}
        libelle="Colette"
        icone={(c) => <NavColette taille={22} couleur={c} />}
        onClick={() => aller('reglages')}
      />
    </nav>
  );
}
